// src/app/api/baseApi.ts
//
// Single shared RTK Query base API.
// prepareHeaders checks BOTH state.auth.token (parent portal)
// and state.adminAuth.token (admin portal) — whichever is present
// is sent as the Bearer token. Admin routes take precedence when
// both exist (which should never happen in practice).

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import { deviceId } from "../deviceId";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as any;

    // Admin portal token takes priority
    const adminToken = state?.adminAuth?.token;
    const userToken = state?.auth?.token;
    const token = adminToken ?? userToken;

    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    // Lets the server spot a sign-in from a new device (see deviceId.ts)
    headers.set("x-device-id", deviceId());
    return headers;
  },
});

/* ── Re-auth wrapper ──────────────────────────────────────────────
   On 401: dispatch the correct logout action depending on which
   slice owns the token that was used.
─────────────────────────────────────────────────────────────────── */
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const state = api.getState() as any;
    const isAdmin = Boolean(state?.adminAuth?.token);

    if (isAdmin) {
      // Clear admin session
      api.dispatch({ type: "adminAuth/clearAdminCredentials" });
    } else {
      // Clear parent/user/learner session (authSlice.clearCredentials)
      api.dispatch({ type: "auth/clearCredentials" });
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    // ── Parent portal ──────────────────────
    "User",
    "ParentDashboard",
    "ParentChildren",
    "ParentEnrolments",
    "ParentPayments",
    "ParentShowcases",
    "ParentMessages",
    "ParentProfile",
    // ── Shared / enrolments ────────────────
    "Enrolments",
    "Enrollment",
    "Track",
    "Interest",
    // ── Admin portal ───────────────────────
    "Students",
    "AdminPayments",
    "AdminDashboard",
    "ProgressReports",
    "QuarterSchedules",
    "Attendance",
    "PaymentDuePreview",
    "AdminCurriculum",
    "AdminShowcases",
    "AdminParents",
    "ParentMessages",
    "ParentProfile", "AdminMessages",
  ],
  endpoints: () => ({}),
});
